import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';

import { useToast } from '@components/Toast';

import { fetchExportStatus } from '../api/api';

const STORAGE_KEY = 'metricsExplorerPendingExports';
const PENDING_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

interface PendingExport {
  key: string;
  createdAt: number;
}

interface ExportNotificationContextValue {
  trackExport: (key: string) => void;
}

const ExportNotificationContext =
  createContext<ExportNotificationContextValue | null>(null);

const savePendingExports = (exports: PendingExport[]) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(exports));
  } catch {
    // Ignore storage errors
  }
};

const loadPendingExports = (): PendingExport[] => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed: PendingExport[] = JSON.parse(stored);
    // Discard any exports that have been pending for more than 1 hour
    const now = Date.now();
    const filtered = parsed.filter(
      (e) => now - e.createdAt < PENDING_TIMEOUT_MS,
    );
    if (filtered.length !== parsed.length) {
      savePendingExports(filtered);
    }
    return filtered;
  } catch {
    return [];
  }
};

/**
 * Polls a single pending export key and returns its status.
 * Polls every 5 seconds while the export is still pending.
 */
const usePollExportKey = (key: string | undefined) => {
  return useQuery({
    queryKey: ['exportNotification', 'poll', key],
    queryFn: () => {
      if (!key) {
        throw new Error('Key is required');
      }
      return fetchExportStatus(key);
    },
    enabled: !!key,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'Pending' ? 5000 : false;
    },
    staleTime: 0,
    gcTime: 0,
    retry: 3,
  });
};

/**
 * Internal component that polls a single export key and fires
 * a toast notification when it completes or errors.
 */
const ExportKeyPoller = ({
  exportKey,
  onSettled,
}: {
  exportKey: string;
  onSettled: (key: string) => void;
}) => {
  const toast = useToast();
  const { data, isError } = usePollExportKey(exportKey);
  const hasNotifiedRef = useRef(false);

  useEffect(() => {
    if (hasNotifiedRef.current) {
      return;
    }

    if (isError) {
      hasNotifiedRef.current = true;
      toast.error('Your metrics explorer export failed. Please try again.');
      onSettled(exportKey);
      return;
    }

    if (!data) {
      return;
    }

    if (data.status === 'Complete') {
      hasNotifiedRef.current = true;
      toast.success('Your metrics explorer export has completed.');
      onSettled(exportKey);
    } else if (data.status === 'Error') {
      hasNotifiedRef.current = true;
      toast.error(
        data.error_message ||
          'Your metrics explorer export failed. Please try again.',
      );
      onSettled(exportKey);
    }
  }, [data, isError, exportKey, onSettled, toast]);

  return null;
};

export const ExportNotificationProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [pendingExports, setPendingExports] =
    useState<PendingExport[]>(loadPendingExports);

  const trackExport = useCallback((key: string) => {
    setPendingExports((prev) => {
      if (prev.some((e) => e.key === key)) {
        return prev;
      }
      const next = [...prev, { key, createdAt: Date.now() }];
      savePendingExports(next);
      return next;
    });
  }, []);

  const handleSettled = useCallback((key: string) => {
    setPendingExports((prev) => {
      const next = prev.filter((e) => e.key !== key);
      savePendingExports(next);
      return next;
    });
  }, []);

  return (
    <ExportNotificationContext.Provider value={{ trackExport }}>
      {children}
      {pendingExports.map((pending) => (
        <ExportKeyPoller
          key={pending.key}
          exportKey={pending.key}
          onSettled={handleSettled}
        />
      ))}
    </ExportNotificationContext.Provider>
  );
};

export const useExportNotification = (): ExportNotificationContextValue => {
  const context = useContext(ExportNotificationContext);
  if (!context) {
    throw new Error(
      'useExportNotification must be used within an ExportNotificationProvider',
    );
  }
  return context;
};
